
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Scale } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { NutritionJournal } from '@/types';
import useUserProfileSync from '@/hooks/useUserProfileSync';

interface WeightTabProps {
  journal: NutritionJournal;
  setJournal: (journal: NutritionJournal) => void;
  containerVariants: any;
  itemVariants: any;
}

const WeightTab: React.FC<WeightTabProps> = ({ journal, setJournal, containerVariants, itemVariants }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bmi, setBmi] = useState(0);
  
  const { getSyncedProfile } = useUserProfileSync({
    userId: user?.id,
    enabled: !!user?.id,
    onProfileUpdated: (profile) => {
      if (profile && profile.height > 0 && profile.weight > 0) {
        const newBmi = profile.weight / Math.pow(profile.height/100, 2);
        setBmi(newBmi);
      }
    }
  });
  
  const profile = getSyncedProfile();
  // Read weight directly from profile (single source of truth)
  const weight = profile?.weight || 0;
  const height = profile?.height || 0;
  
  useEffect(() => {
    if (height > 0 && weight > 0) {
      const calculatedBmi = weight / Math.pow(height/100, 2);
      setBmi(calculatedBmi);
    }
  }, [weight, height]);

  const formatDate = (date: Date) => {
    return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const getBmiStatus = (bmi: number) => {
    if (bmi < 16) return 'Bajo peso severo';
    if (bmi < 18.5) return 'Bajo peso';
    if (bmi <= 24.9) return 'Peso normal';
    if (bmi <= 29.9) return 'Sobrepeso';
    if (bmi <= 34.9) return 'Obesidad grado I';
    if (bmi <= 39.9) return 'Obesidad grado II';
    return 'Obesidad grado III';
  };

  const handleProfileSettings = () => {
    navigate('/nutricion');
  };

  const generateWeightHistory = () => {
    if (weight <= 0) return [{ date: "Sin datos", weight: 0 }];
    
    const now = new Date();
    return [
      { date: formatDate(new Date(now.getTime() - 35 * 24 * 60 * 60 * 1000)), weight: weight + 2.0 },
      { date: formatDate(new Date(now.getTime() - 28 * 24 * 60 * 60 * 1000)), weight: weight + 1.7 },
      { date: formatDate(new Date(now.getTime() - 21 * 24 * 60 * 60 * 1000)), weight: weight + 1.2 },
      { date: formatDate(new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000)), weight: weight + 0.7 },
      { date: formatDate(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)), weight: weight + 0.3 },
      { date: formatDate(now), weight: weight },
    ];
  };

  const weightHistoryData = generateWeightHistory();
  const weightTrend = weightHistoryData.length >= 2 ? 
    weightHistoryData[weightHistoryData.length - 1].weight - weightHistoryData[weightHistoryData.length - 2].weight : 0;

  return (
    <div className="p-5">
      <Card className="border-0 shadow-lg rounded-2xl overflow-hidden bg-gradient-to-br from-green-100/50 to-green-50 backdrop-blur-sm border border-green-200">
        <CardHeader className="pb-2 bg-gradient-to-r from-green-500/90 to-green-600/90 text-white">
          <CardTitle className="text-base flex items-center text-white">
            {weightTrend < 0 ? (
              <TrendingDown className="w-5 h-5 mr-2" />
            ) : (
              <TrendingUp className="w-5 h-5 mr-2" />
            )}
            Evolución del Peso
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5">
          <div className="mb-2 flex items-center justify-center">
            <div className="text-lg font-medium">
              <Scale className="h-5 w-5 inline mr-2 text-green-600" />
              {weight > 0 ? `${weight} kg - IMC: ${bmi.toFixed(1)} (${getBmiStatus(bmi)})` : 'Sin datos de peso'}
            </div>
          </div>
          
          <div className="h-64">
            {weight > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={weightHistoryData}
                  margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#9ca3af"/>
                  <YAxis domain={['dataMin - 2', 'dataMax + 2']} tick={{ fontSize: 12 }} stroke="#9ca3af"/>
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="weight"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ r: 4, strokeWidth: 2, fill: 'white' }}
                    activeDot={{ r: 6, strokeWidth: 2, fill: '#10b981' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                <p>Para ver tu evolución, configura tu peso en la sección de Perfil</p>
              </div>
            )}
          </div>

          <div className="mt-4 p-3 bg-green-50 border border-green-100 rounded-md">
            <div className="flex flex-col items-center text-sm text-green-700">
              <p className="text-center mb-3">
                Para actualizar tu peso y ver tu evolución completa, dirígete a tu perfil nutricional.
              </p>
              <Button 
                onClick={handleProfileSettings}
                variant="mint" 
                className="mt-2"
              >
                Ir a Perfil Nutricional
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default React.memo(WeightTab);
